import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material';
import { SyntheticEvent, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TabWrapper from '../../../shared/TabWrapper/TabWrapper';
import CreateNewPipeline, { defaultCreateFormData } from './CreateNewPipeline';
import ImportPipeline, { defaultImportFormData } from './ImportPipeline';
import ClonePipeline, { defaultCloneFormData } from './ClonePipeline';
import { AddPipelineType } from './types';
import useGetPipelinesByEmail from '../../../shared/hooks/useGetPipelinesByEmail';
import { useAppSelector } from '../../../store/hooks';
import { useHistory } from 'react-router-dom';
import { preparePipelines, validate } from './formProcessingService';
import ErrorTypography from '../../../shared/ErrorTypography/ErrorTypography';
import UpTransition from '../../../shared/UpTransition/UpTransition';
import { Trans, useTranslation } from 'react-i18next';
import { useSavePipelineMutation } from '../../../store/data/dataSlice';

type Props = {
  open: boolean;
  onClose: () => void;
};

const initialTabData = {
  0: defaultCreateFormData,
  1: defaultImportFormData,
  2: defaultCloneFormData,
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const CreatePipelineDialog = (props: Props): JSX.Element => {
  const { t } = useTranslation();

  const [tabValue, setTabValue] = useState(0);
  const [tabData, setTabData] = useState(initialTabData);
  const [error, setError] = useState(null as string | null);

  const [createPipeline] = useSavePipelineMutation();

  const pipelines = useGetPipelinesByEmail();
  const authenticated = useAppSelector(state => state.usertoken.authenticated);
  const user = useAppSelector(state => state.usertoken.token);

  const routerHistory = useHistory();

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = (success: boolean) => async () => {
    if (success) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const addData = tabData[tabValue];
      if (!validate(addData)) {
        setError(t('Please check your input!'));
        return;
      } else {
        if(authenticated && user) {
          try {
            const pipeline = await preparePipelines(addData, pipelines.data, user);
            await createPipeline({ pid: pipeline.pid, pipeline }).unwrap();
            // TODO: does not work -> redirect is too fast
            routerHistory.push('/pipeline/' + pipeline.pid);
          } catch (e) {
            // TODO: ist das Error-Handling zu verbose
            setError(t('Error creating pipeline:') + ' ' + t((e as Error).message));
          }
        }
      }
    }
    setError(null);
    setTabValue(0);
    setTabData(initialTabData);
    props.onClose();
  };

  const handleDataChange = (tabId: number) => (e: AddPipelineType) => {
    const newData = { ...tabData };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    newData[tabId] = e;
    setTabData(newData);
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose(false)}
      fullWidth={true}
      maxWidth={'md'}
      disableEnforceFocus
      TransitionComponent={UpTransition}
    >
      <div className='pipelinedialog'>
      <DialogTitle>
        <Trans>Add Pipeline</Trans>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label={'import tabs'}>
            <Tab label={t('Create')} icon={<AddIcon />} {...a11yProps(0)} />
            <Tab label={t('Import')} icon={<ArrowDownwardIcon />} {...a11yProps(1)} />
            <Tab label={t('Copy')} icon={<ContentCopyIcon />} {...a11yProps(2)} />
          </Tabs>
        </Box>
        <TabWrapper index={0} value={tabValue}>
          <CreateNewPipeline data={tabData[0]} onChange={handleDataChange(0)} />
        </TabWrapper>
        <TabWrapper index={1} value={tabValue}>
          <ImportPipeline data={tabData[1]} onChange={handleDataChange(1)} />
        </TabWrapper>
        <TabWrapper index={2} value={tabValue}>
          <ClonePipeline data={tabData[2]} onChange={handleDataChange(2)} pipelines={pipelines} />
        </TabWrapper>
        {error && <ErrorTypography message={error} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose(false)}><Trans>Cancel</Trans></Button>
        <Button onClick={handleClose(true)}><Trans>Create pipeline</Trans></Button>
      </DialogActions>
      </div>
    </Dialog>
  );
};

export default CreatePipelineDialog;
